/**
 * AZURE AD USER SYNC COMPONENT
 * 
 * Allows admins to:
 * - Sync all users from Azure AD tenant
 * - Sync users from specific Azure AD security group
 * - Test Azure AD connection
 */

import React, { useState, useEffect } from 'react';
import { Users, RefreshCw, CheckCircle, XCircle, AlertCircle, Download, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../api/client';

const AzureAdUserSync = () => {
  const { currentCompanyId } = useAuth();
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [groups, setGroups] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [syncType, setSyncType] = useState('all'); // 'all' or 'group'
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [syncResults, setSyncResults] = useState(null);
  const [error, setError] = useState('');

  // Load Azure AD groups and company roles
  useEffect(() => {
    loadAzureGroups();
    loadCompanyRoles();
  }, []);

  const loadAzureGroups = async () => {
    try {
      const response = await apiClient.get('/user-sync/azure-groups');
      const groupsData = response.data.groups || response.data;
      setGroups(Array.isArray(groupsData) ? groupsData : []);
    } catch (err) {
      console.error('Failed to load Azure AD groups:', err);
      // Not critical - user can still sync all users
    }
  };

  const loadCompanyRoles = async () => {
    try {
      const companyId = currentCompanyId || localStorage.getItem('currentCompanyId');
      const url = companyId ? `/roles?companyId=${companyId}` : '/roles';
      const response = await apiClient.get(url);
      const rolesData = response.data.roles || response.data;
      setRoles(Array.isArray(rolesData) ? rolesData : []);
    } catch (err) {
      console.error('Failed to load roles:', err);
    }
  };

  const testConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    setError('');

    try {
      const response = await apiClient.get('/user-sync/test-connection');

      setConnectionStatus({
        success: true,
        message: response.data.message,
        tenantId: response.data.tenantId
      });
    } catch (err) {
      setConnectionStatus({
        success: false,
        message: err.response?.data?.message || 'Connection failed'
      });
      setError(err.response?.data?.message || 'Failed to connect to Azure AD');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSync = async () => {
    setLoading(true);
    setError('');
    setSyncResults(null);

    const effectiveCompanyId = currentCompanyId || localStorage.getItem('currentCompanyId');
    if (!effectiveCompanyId) {
      setError('No company selected. Please select a company before syncing.');
      setLoading(false);
      return;
    }

    try {
      const response = await apiClient.post(
        '/user-sync/azure-ad',
        {
          groupId: syncType === 'group' ? selectedGroup : null,
          defaultRoleId: selectedRole || null,
          companyId: effectiveCompanyId
        }
      );

      setSyncResults(response.data.results);
    } catch (err) {
      console.error('Sync error:', err);
      setError(err.response?.data?.message || 'Failed to sync users from Azure AD');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-7 h-7 text-blue-600" />
            Azure AD User Sync
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Import users from your Azure Active Directory tenant
          </p>
        </div>
      </div>

      {/* Connection Test */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Connection Status</h3>
        
        <button
          onClick={testConnection}
          disabled={testingConnection}
          className="btn btn-primary"
        >
          <RefreshCw className={`w-4 h-4 ${testingConnection ? 'animate-spin' : ''}`} />
          Test Azure AD Connection
        </button>

        {connectionStatus && (
          <div className={`mt-4 p-4 rounded-lg ${
            connectionStatus.success 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-start gap-2">
              {connectionStatus.success ? (
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
              )}
              <div>
                <p className={`font-medium ${
                  connectionStatus.success ? 'text-green-900' : 'text-red-900'
                }`}>
                  {connectionStatus.message}
                </p>
                {connectionStatus.tenantId && (
                  <p className="text-sm text-green-700 mt-1">
                    Tenant ID: {connectionStatus.tenantId}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sync Configuration */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Sync Configuration</h3>

        <div className="space-y-4">
          {/* Sync Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sync Type
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="all"
                  checked={syncType === 'all'}
                  onChange={(e) => setSyncType(e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">All Users in Tenant</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="group"
                  checked={syncType === 'group'}
                  onChange={(e) => setSyncType(e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Specific Security Group</span>
              </label>
            </div>
          </div>

          {/* Group Selection */}
          {syncType === 'group' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Azure AD Group
              </label>
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">-- Select a group --</option>
                {groups.map(group => (
                  <option key={group.id} value={group.id}>
                    {group.displayName}
                    {group.description && ` - ${group.description}`}
                  </option>
                ))}
              </select>
              {groups.length === 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  Loading groups... If groups don't appear, check Azure AD permissions.
                </p>
              )}
            </div>
          )}

          {/* Default Role */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Role (Optional)
              <span className="text-xs text-gray-500 ml-2">
                Assign this role to newly created users
              </span>
            </label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">-- No default role --</option>
              {roles.map(role => (
                <option key={role.RoleID} value={role.RoleID}>
                  {role.RoleName}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              If not selected, users will be created without role assignment
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">What happens during sync:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-800">
                  <li>New users will be created with SSO authentication</li>
                  <li>Existing users will be updated (name, email, status)</li>
                  <li>Users are matched by email address or Azure AD Object ID</li>
                  <li>Default role will be assigned to newly created users only</li>
                  <li>Existing role assignments will not be modified</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Sync Button */}
          <button
            onClick={handleSync}
            disabled={loading || (syncType === 'group' && !selectedGroup)}
            className="btn btn-success btn-lg w-full"
          >
            <Download className={`w-5 h-5 ${loading ? 'animate-bounce' : ''}`} />
            {loading ? 'Syncing Users...' : 'Start User Sync'}
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <p className="font-medium text-red-900">Sync Failed</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Sync Results */}
      {syncResults && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CheckCircle className="w-6 h-6 text-green-600" />
            Sync Completed
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-blue-600">
                {syncResults.total}
              </div>
              <div className="text-sm text-blue-900 mt-1">Total Users</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-green-600">
                {syncResults.created}
              </div>
              <div className="text-sm text-green-900 mt-1">Created</div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-yellow-600">
                {syncResults.updated}
              </div>
              <div className="text-sm text-yellow-900 mt-1">Updated</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-red-600">
                {syncResults.failed}
              </div>
              <div className="text-sm text-red-900 mt-1">Failed</div>
            </div>
          </div>

          {syncResults.errors && syncResults.errors.length > 0 && (
            <div className="mt-4">
              <h4 className="font-medium text-gray-900 mb-2">Errors:</h4>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                <ul className="space-y-1 text-sm text-red-800">
                  {syncResults.errors.map((err, idx) => (
                    <li key={idx}>
                      <span className="font-medium">{err.user}:</span> {err.error}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AzureAdUserSync;
